import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import apiClient from '../../utils/api';
import './CheckoutPage.css';

function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreditCard, setShowCreditCard] = useState(false);
  const [order, setOrder] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  
  // Address state
  const [billingAddress, setBillingAddress] = useState({
    first_name: '',
    last_name: '',
    company: '',
    street: '',
    street_number: '',
    additional_info: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    phone: '',
    email: user?.email || ''
  });
  const [shippingAddress, setShippingAddress] = useState({
    first_name: '',
    last_name: '',
    company: '',
    street: '',
    street_number: '',
    additional_info: '',
    postal_code: '',
    city: '',
    country: 'Deutschland'
  });
  const [useBillingAsShipping, setUseBillingAsShipping] = useState(true);
  const [addressErrors, setAddressErrors] = useState({});
  const [orderNotes, setOrderNotes] = useState('');  // Customer notes for the order

  // Get order details from navigation state
  const orderDetails = location.state?.orderDetails;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!orderDetails) {
      navigate(location.pathname.includes('coins') ? '/coins' : '/pricing');
      return;
    }

    // Fetch PayPal settings on mount and when coming back
    fetchPayPalSettings();
    
    // Also fetch when window regains focus (user comes back from admin panel)
    const handleFocus = () => {
      fetchPayPalSettings();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, orderDetails, navigate]);

  const fetchPayPalSettings = async () => {
    try {
      console.log('🔄 Lade PayPal-Einstellungen...');
      const settings = await apiClient.getPublicPaymentSettings();
      console.log('📦 Empfangene Settings:', settings);
      
      if (settings.paypal_enabled && settings.paypal_client_id) {
        setPaypalClientId(settings.paypal_client_id);
        setSettingsLoaded(true);
        console.log('✅ PayPal aktiviert! Client ID:', settings.paypal_client_id.substring(0, 20) + '...');
      } else {
        setPaypalClientId(null);
        setSettingsLoaded(true);
        console.log('❌ PayPal nicht verfügbar. Enabled:', settings.paypal_enabled, 'Client ID vorhanden:', !!settings.paypal_client_id);
      }
      
      // Force re-render
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('❌ Fehler beim Laden der PayPal-Einstellungen:', error);
      setPaypalClientId(null);
      setSettingsLoaded(true);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponValid(null);
      setAppliedDiscount(0);
      return;
    }

    try {
      const result = await apiClient.request(`/api/affiliate/validate-coupon/${couponCode.toUpperCase()}`);
      setCouponValid(result);
      
      if (result.valid) {
        setAppliedDiscount(result.discount_percentage);
      } else {
        setAppliedDiscount(0);
      }
    } catch (error) {
      setCouponValid({ valid: false, message: 'Code ungültig' });
      setAppliedDiscount(0);
    }
  };

  const calculateFinalPrice = () => {
    const basePrice = orderDetails.amount;
    if (appliedDiscount > 0) {
      return basePrice * (1 - appliedDiscount / 100);
    }
    return basePrice;
  };

  // Validate billing address
  const validateBillingAddress = () => {
    const errors = {};
    
    if (!billingAddress.first_name?.trim()) errors.first_name = 'Vorname erforderlich';
    if (!billingAddress.last_name?.trim()) errors.last_name = 'Nachname erforderlich';
    if (!billingAddress.street?.trim()) errors.street = 'Straße erforderlich';
    if (!billingAddress.street_number?.trim()) errors.street_number = 'Hausnummer erforderlich';
    if (!billingAddress.postal_code?.trim()) errors.postal_code = 'PLZ erforderlich';
    if (!billingAddress.city?.trim()) errors.city = 'Stadt erforderlich';
    
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleMethodSelect = async (method) => {
    // Validate address first
    if (!validateBillingAddress()) {
      return;
    }
    
    // Reset states when switching methods
    setOrder(null);
    setShowCreditCard(false);
    setSelectedMethod(method);
    
    // For PayPal and Credit Card: immediately create order and show payment UI
    if (method === 'paypal' || method === 'credit_card') {
      setLoading(true);
      
      // Set credit card flag before creating order
      if (method === 'credit_card') {
        setShowCreditCard(true);
      }
      
      try {
        await createOrder('paypal');
      } catch (error) {
        console.error('Error creating order:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleProceedToPayment = async () => {
    if (!selectedMethod) return;
    if (selectedMethod !== 'bank_transfer') return;

    setLoading(true);
    await createOrder('bank_transfer');
  };

  const createOrder = async (paymentMethod) => {
    setLoading(true);
    try {
      const orderType = orderDetails.order_type; // 'subscription', 'onetime', or 'coins'
      
      // Handle Coins purchase separately
      if (orderType === 'coins') {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credits/purchase`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            package_id: orderDetails.package_id,
            payment_method: paymentMethod,
            coupon_code: couponCode || null,
            billing_address: billingAddress,
            shipping_address: useBillingAsShipping ? null : shippingAddress,
            use_billing_as_shipping: useBillingAsShipping,
            customer_notes: orderNotes || null
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Fehler beim Erstellen der Bestellung');
        }

        const result = await response.json();
        setOrder(result);

        // If bank transfer, redirect immediately
        if (paymentMethod === 'bank_transfer') {
          navigate('/checkout/bank-transfer', { 
            state: { 
              order: result,
              amount: calculateFinalPrice(),
              orderNumber: result.order_number,
              isCoins: true,
              packageData: orderDetails.package
            } 
          });
        }
        setLoading(false);
        return;
      }
      
      // Handle subscriptions and onetime products
      const isOnetime = orderType === 'onetime';
      
      const items = [{
        product_type: isOnetime ? 'onetime' : 'subscription',
        product_id: orderDetails.product_id || undefined,
        product_name: isOnetime ? orderDetails.product_name : orderDetails.planName,
        quantity: 1,
        unit_price: orderDetails.amount,
        total_price: calculateFinalPrice()
      }];

      const result = await apiClient.createOrder({
        order_type: isOnetime ? 'onetime' : 'subscription',
        items,
        payment_method: paymentMethod,
        discount: appliedDiscount,
        coupon_code: couponCode || null,
        billing_address: billingAddress,
        shipping_address: useBillingAsShipping ? null : shippingAddress,
        use_billing_as_shipping: useBillingAsShipping,
        customer_notes: orderNotes || null
      });

      setOrder(result);

      // If PayPal subscription, redirect to PayPal approval URL
      if (paymentMethod === 'paypal' && result.is_subscription && result.paypal_approval_url) {
        console.log('🎉 Redirecting to PayPal approval URL:', result.paypal_approval_url);
        window.location.href = result.paypal_approval_url;
        return;
      }

      // If PayPal onetime payment, redirect to PayPal approval URL
      if (paymentMethod === 'paypal' && isOnetime && result.paypal_approval_url) {
        console.log('🎉 Redirecting to PayPal for onetime payment:', result.paypal_approval_url);
        window.location.href = result.paypal_approval_url;
        return;
      }

      // If bank transfer, redirect immediately
      if (paymentMethod === 'bank_transfer') {
        navigate('/checkout/bank-transfer', { 
          state: { 
            order: result,
            amount: calculateFinalPrice(),
            orderNumber: result.order_number,
            isOnetime: isOnetime
          } 
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error creating order:', error);
      navigate('/checkout/error', {
        state: {
          error: typeof error === 'string' ? error : (error.message || error.response?.data?.detail || 'Fehler beim Erstellen der Bestellung'),
          planName: orderDetails.planName
        }
      });
      setLoading(false);
    }
  };

  const handlePayPalApprove = async (data, actions) => {
    try {
      // Handle Coins purchase
      if (orderDetails.order_type === 'coins') {
        const details = await actions.order.capture();
        
        console.log('✅ PayPal capture successful:', details);
        
        // Capture payment on backend
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paypal/capture-payment`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            order_id: order.order_id,
            payment_id: details.id,
            payer_id: details.payer.payer_id
          })
        });

        const result = await response.json();
        
        if (!response.ok) {
          console.error('❌ Backend capture failed:', result);
          throw new Error(result.detail || 'Backend-Fehler bei der Zahlungsverarbeitung');
        }
        
        console.log('✅ Backend capture successful:', result);
        
        navigate('/checkout/success', {
          state: {
            planName: orderDetails.planName,
            amount: orderDetails.amount,
            paymentMethod: 'PayPal',
            isCoins: true
          }
        });
        return;
      }
      
      // Handle subscriptions and onetime products
      await apiClient.capturePayPalPayment(
        data.paymentID,
        data.payerID,
        order.id
      );
      
      navigate('/checkout/success', {
        state: {
          planName: orderDetails.planName,
          amount: orderDetails.amount,
          paymentMethod: 'PayPal'
        }
      });
    } catch (error) {
      console.error('PayPal capture error:', error);
      navigate('/checkout/error', {
        state: {
          error: typeof error === 'string' ? error : (error.message || 'Zahlung konnte nicht abgeschlossen werden'),
          planName: orderDetails.planName
        }
      });
    }
  };

  if (!orderDetails) {
    return null;
  }

  return (
    <div className="checkout-page" data-testid="checkout-page">
      <div className="checkout-container">
        <div className="checkout-header-actions">
          <button 
            onClick={() => navigate(orderDetails.order_type === 'coins' ? '/coins' : '/pricing')} 
            className="btn-back"
            data-testid="back-button"
          >
            ← Zurück
          </button>
          <button 
            onClick={fetchPayPalSettings} 
            className="btn-refresh"
            data-testid="refresh-button"
            title="Zahlungsmethoden aktualisieren"
          >
            🔄 Aktualisieren
          </button>
        </div>

        <div className="checkout-header">
          <h1 className="checkout-title">{t('checkout.title')}</h1>
          <div className="order-summary">
            <p className="plan-name">
              {orderDetails.order_type === 'coins' ? orderDetails.planName : 
               orderDetails.order_type === 'onetime' ? orderDetails.product_name : 
               orderDetails.planName}
            </p>
            <p className="plan-price">
              {appliedDiscount > 0 && (
                <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: '8px' }}>
                  {orderDetails.amount.toFixed ? orderDetails.amount.toFixed(2) : orderDetails.amount}€
                </span>
              )}
              <span>
                {calculateFinalPrice().toFixed(2)}€
              </span>
              {' '}
              {orderDetails.order_type === 'coins' || orderDetails.order_type === 'onetime' ? ' einmalig' : 
               ` / ${orderDetails.interval === 'monthly' ? 'Monat' : 'Jahr'}`}
            </p>
            {appliedDiscount > 0 && (
              <p className="discount-applied" style={{ color: '#4ade80', fontSize: '14px', marginTop: '8px' }}>
                🎉 {appliedDiscount}% Rabatt angewendet!
              </p>
            )}
          </div>
        </div>

        {/* Billing Address Section */}
        <div className="address-section" data-testid="billing-address-section">
          <h2 className="address-title">📋 {t('checkout.billingAddress')}</h2>
          <div className="address-form">
            <div className="form-row">
              <div className="form-group">
                <label>Vorname *</label>
                <input
                  type="text"
                  value={billingAddress.first_name}
                  onChange={(e) => setBillingAddress({...billingAddress, first_name: e.target.value})}
                  className={addressErrors.first_name ? 'error' : ''}
                  placeholder="Max"
                  data-testid="billing-first-name"
                />
                {addressErrors.first_name && <span className="error-text">{addressErrors.first_name}</span>}
              </div>
              <div className="form-group">
                <label>Nachname *</label>
                <input
                  type="text"
                  value={billingAddress.last_name}
                  onChange={(e) => setBillingAddress({...billingAddress, last_name: e.target.value})}
                  className={addressErrors.last_name ? 'error' : ''}
                  placeholder="Mustermann"
                  data-testid="billing-last-name"
                />
                {addressErrors.last_name && <span className="error-text">{addressErrors.last_name}</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label>Firma (optional)</label>
              <input
                type="text"
                value={billingAddress.company}
                onChange={(e) => setBillingAddress({...billingAddress, company: e.target.value})}
                placeholder="Firma GmbH"
                data-testid="billing-company"
              />
            </div>
            
            <div className="form-row">
              <div className="form-group flex-2">
                <label>Straße *</label>
                <input
                  type="text"
                  value={billingAddress.street}
                  onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
                  className={addressErrors.street ? 'error' : ''}
                  placeholder="Musterstraße"
                  data-testid="billing-street"
                />
                {addressErrors.street && <span className="error-text">{addressErrors.street}</span>}
              </div>
              <div className="form-group flex-1">
                <label>Nr. *</label>
                <input
                  type="text"
                  value={billingAddress.street_number}
                  onChange={(e) => setBillingAddress({...billingAddress, street_number: e.target.value})}
                  className={addressErrors.street_number ? 'error' : ''}
                  placeholder="123"
                  data-testid="billing-street-number"
                />
                {addressErrors.street_number && <span className="error-text">{addressErrors.street_number}</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label>Adresszusatz (optional)</label>
              <input
                type="text"
                value={billingAddress.additional_info}
                onChange={(e) => setBillingAddress({...billingAddress, additional_info: e.target.value})}
                placeholder="Apartment 4B, Hinterhaus, etc."
                data-testid="billing-additional"
              />
            </div>
            
            <div className="form-row">
              <div className="form-group flex-1">
                <label>PLZ *</label>
                <input
                  type="text"
                  value={billingAddress.postal_code}
                  onChange={(e) => setBillingAddress({...billingAddress, postal_code: e.target.value})}
                  className={addressErrors.postal_code ? 'error' : ''}
                  placeholder="12345"
                  data-testid="billing-postal-code"
                />
                {addressErrors.postal_code && <span className="error-text">{addressErrors.postal_code}</span>}
              </div>
              <div className="form-group flex-2">
                <label>Stadt *</label>
                <input
                  type="text"
                  value={billingAddress.city}
                  onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                  className={addressErrors.city ? 'error' : ''}
                  placeholder="Berlin"
                  data-testid="billing-city"
                />
                {addressErrors.city && <span className="error-text">{addressErrors.city}</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label>Land</label>
              <select
                value={billingAddress.country}
                onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value})}
                data-testid="billing-country"
              >
                <option value="Deutschland">Deutschland</option>
                <option value="Österreich">Österreich</option>
                <option value="Schweiz">Schweiz</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Telefon (optional)</label>
              <input
                type="tel"
                value={billingAddress.phone}
                onChange={(e) => setBillingAddress({...billingAddress, phone: e.target.value})}
                placeholder="+49 123 456789"
                data-testid="billing-phone"
              />
            </div>
          </div>
        </div>

        {/* Shipping Address Toggle */}
        <div className="shipping-toggle" data-testid="shipping-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={useBillingAsShipping}
              onChange={(e) => setUseBillingAsShipping(e.target.checked)}
              data-testid="use-billing-as-shipping"
            />
            <span className="toggle-text">{t('checkout.sameAsBilling')}</span>
          </label>
        </div>

        {/* Shipping Address (only if different from billing) */}
        {!useBillingAsShipping && (
          <div className="address-section shipping-address" data-testid="shipping-address-section">
            <h2 className="address-title">📦 {t('checkout.shippingAddress')}</h2>
            <div className="address-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Vorname *</label>
                  <input
                    type="text"
                    value={shippingAddress.first_name}
                    onChange={(e) => setShippingAddress({...shippingAddress, first_name: e.target.value})}
                    placeholder="Max"
                    data-testid="shipping-first-name"
                  />
                </div>
                <div className="form-group">
                  <label>Nachname *</label>
                  <input
                    type="text"
                    value={shippingAddress.last_name}
                    onChange={(e) => setShippingAddress({...shippingAddress, last_name: e.target.value})}
                    placeholder="Mustermann"
                    data-testid="shipping-last-name"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Firma (optional)</label>
                <input
                  type="text"
                  value={shippingAddress.company}
                  onChange={(e) => setShippingAddress({...shippingAddress, company: e.target.value})}
                  placeholder="Firma GmbH"
                  data-testid="shipping-company"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group flex-2">
                  <label>Straße *</label>
                  <input
                    type="text"
                    value={shippingAddress.street}
                    onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                    placeholder="Musterstraße"
                    data-testid="shipping-street"
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Nr. *</label>
                  <input
                    type="text"
                    value={shippingAddress.street_number}
                    onChange={(e) => setShippingAddress({...shippingAddress, street_number: e.target.value})}
                    placeholder="123"
                    data-testid="shipping-street-number"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Adresszusatz (optional)</label>
                <input
                  type="text"
                  value={shippingAddress.additional_info}
                  onChange={(e) => setShippingAddress({...shippingAddress, additional_info: e.target.value})}
                  placeholder="Apartment 4B, Hinterhaus, etc."
                  data-testid="shipping-additional"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group flex-1">
                  <label>PLZ *</label>
                  <input
                    type="text"
                    value={shippingAddress.postal_code}
                    onChange={(e) => setShippingAddress({...shippingAddress, postal_code: e.target.value})}
                    placeholder="12345"
                    data-testid="shipping-postal-code"
                  />
                </div>
                <div className="form-group flex-2">
                  <label>Stadt *</label>
                  <input
                    type="text"
                    value={shippingAddress.city}
                    onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                    placeholder="Berlin"
                    data-testid="shipping-city"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Land</label>
                <select
                  value={shippingAddress.country}
                  onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                  data-testid="shipping-country"
                >
                  <option value="Deutschland">Deutschland</option>
                  <option value="Österreich">Österreich</option>
                  <option value="Schweiz">Schweiz</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Order Notes Section */}
        <div className="order-notes-section" data-testid="order-notes-section">
          <h2 className="notes-title">📝 Bestellnotizen (optional)</h2>
          <div className="notes-form">
            <textarea
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder={t('checkout.orderNotesPlaceholder')}
              rows={4}
              maxLength={1000}
              data-testid="order-notes-input"
            />
            <div className="notes-counter">{orderNotes.length}/1000 Zeichen</div>
          </div>
        </div>

        {/* Coupon Code Section */}
        <div className="coupon-section" style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
            🏷️ {t('checkout.couponCode')}
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setCouponValid(null);
                setAppliedDiscount(0);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleValidateCoupon();
                }
              }}
              placeholder="z.B. ALEX10"
              style={{ 
                flex: 1,
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(194, 164, 90, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontFamily: 'monospace',
                letterSpacing: '2px',
                fontSize: '14px'
              }}
              data-testid="coupon-input"
            />
            <button
              onClick={handleValidateCoupon}
              style={{
                padding: '12px 24px',
                background: 'rgba(194, 164, 90, 0.2)',
                border: '1px solid rgba(194, 164, 90, 0.4)',
                borderRadius: '8px',
                color: '#c2a45a',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              data-testid="validate-coupon-button"
            >
              Anwenden
            </button>
          </div>
          {couponValid && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '14px',
              background: couponValid.valid ? 'rgba(74, 222, 128, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${couponValid.valid ? 'rgba(74, 222, 128, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              color: couponValid.valid ? '#4ade80' : '#ef4444'
            }}>
              {couponValid.message}
            </div>
          )}
        </div>

        <div className="payment-methods" key={refreshKey}>
          {/* PayPal */}
          <div 
            className={`payment-method-card ${
              selectedMethod === 'paypal' ? 'selected' : ''
            } ${!paypalClientId ? 'disabled' : ''}`}
            onClick={() => !loading && paypalClientId && handleMethodSelect('paypal')}
            data-testid="payment-method-paypal"
          >
            <div className="method-icon">💳</div>
            <div className="method-info">
              <h3>PayPal</h3>
              <p>Mit PayPal-Konto zahlen</p>
            </div>
            {!paypalClientId && <span className="badge-disabled">Nicht verfügbar</span>}
          </div>

          {/* Credit Card via PayPal */}
          <div 
            className={`payment-method-card ${
              selectedMethod === 'credit_card' ? 'selected' : ''
            } ${!paypalClientId ? 'disabled' : ''}`}
            onClick={() => !loading && paypalClientId && handleMethodSelect('credit_card')}
            data-testid="payment-method-credit-card"
          >
            <div className="method-icon">💳</div>
            <div className="method-info">
              <h3>Kreditkarte</h3>
              <p>Visa, Mastercard, Amex</p>
            </div>
            {!paypalClientId && <span className="badge-disabled">Nicht verfügbar</span>}
          </div>

          {/* Bank Transfer */}
          <div 
            className={`payment-method-card ${
              selectedMethod === 'bank_transfer' ? 'selected' : ''
            }`}
            onClick={() => !loading && handleMethodSelect('bank_transfer')}
            data-testid="payment-method-bank-transfer"
          >
            <div className="method-icon">🏦</div>
            <div className="method-info">
              <h3>Banküberweisung</h3>
              <p>Manuelle Überweisung</p>
            </div>
          </div>
        </div>

        {/* Proceed Button - Only for Bank Transfer */}
        {selectedMethod === 'bank_transfer' && !order && (
          <div className="proceed-button-container">
            <button
              onClick={handleProceedToPayment}
              className="btn-proceed"
              disabled={loading}
              data-testid="proceed-to-payment-button"
            >
              {loading ? t('checkout.processing') : t('checkout.orderNow')}
            </button>
          </div>
        )}

        {/* PayPal Buttons */}
        {selectedMethod === 'paypal' && order && paypalClientId && !showCreditCard && (
          <div className="paypal-button-container" data-testid="paypal-buttons" key="paypal-standard">
            <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'EUR' }}>
              <PayPalButtons
                createOrder={(data, actions) => {
                  return actions.order.create({
                    purchase_units: [{
                      amount: {
                        value: calculateFinalPrice().toFixed(2),
                        currency_code: 'EUR'
                      },
                      description: orderDetails.planName
                    }]
                  });
                }}
                onApprove={handlePayPalApprove}
                onError={(err) => {
                  console.error('PayPal error:', err);
                  navigate('/checkout/error', {
                    state: {
                      error: 'PayPal-Zahlung fehlgeschlagen',
                      planName: orderDetails.planName
                    }
                  });
                }}
                style={{ layout: 'vertical' }}
              />
            </PayPalScriptProvider>
          </div>
        )}

        {/* Credit Card Form via PayPal */}
        {selectedMethod === 'credit_card' && showCreditCard && order && paypalClientId && (
          <div className="credit-card-container" data-testid="credit-card-form" key="paypal-credit-card">
            <PayPalScriptProvider 
              options={{ 
                clientId: paypalClientId, 
                currency: 'EUR',
                components: 'buttons,card-fields'
              }}
            >
              <PayPalButtons
                fundingSource="card"
                createOrder={(data, actions) => {
                  return actions.order.create({
                    purchase_units: [{
                      amount: {
                        value: calculateFinalPrice().toFixed(2),
                        currency_code: 'EUR'
                      },
                      description: orderDetails.planName
                    }]
                  });
                }}
                onApprove={handlePayPalApprove}
                onError={(err) => {
                  console.error('Card payment error:', err);
                  navigate('/checkout/error', {
                    state: {
                      error: 'Kreditkarten-Zahlung fehlgeschlagen',
                      planName: orderDetails.planName
                    }
                  });
                }}
              />
            </PayPalScriptProvider>
          </div>
        )}

        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Bestellung wird erstellt...</p>
          </div>
        )}

        <div className="checkout-footer">
          <p className="security-note">🔒 Sichere SSL-verschlüsselte Zahlung</p>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;